import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { openDB } from 'idb';
import axios from 'axios';

const OfflineContext = createContext();

const DB_NAME = 'logical-tms-offline';
const DB_VERSION = 2;

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('pending_entries')) {
        db.createObjectStore('pending_entries', {
          keyPath: 'local_id',
          autoIncrement: true,
        });
      }
      if (!db.objectStoreNames.contains('logbook_cache')) {
        db.createObjectStore('logbook_cache', { keyPath: 'id' });
      }
    },
  });
}

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPendingCount = useCallback(async () => {
    const db = await getDB();
    const count = await db.count('pending_entries');
    setPendingCount(count);
  }, []);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  const syncPendingEntries = useCallback(async () => {
    if (syncing) return;
    if (!navigator.onLine) return;

    setSyncing(true);
    try {
      const db = await getDB();
      const pending = await db.getAll('pending_entries');
      const unsynced = pending.filter(e => !e.synced);

      let syncedCount = 0;
      for (const entry of unsynced) {
        try {
          const { local_id, created_offline_at, synced, ...payload } = entry;
          console.log('Szinkronizálás payload:', JSON.stringify(payload));

          const response = await axios.post(
            `${API_BASE}/log-entries/`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              },
            }
          );

          console.log('Response status:', response.status);
          console.log('Response data:', response.data);

          if (response.status >= 200 && response.status < 300) {
            await db.delete('pending_entries', local_id);
            syncedCount++;
          } else {
            console.error('Sikertelen szinkronizálás:', response.status, response.data);
          }
        } catch (err) {
          if (err.response) {
            console.error('Sikertelen szinkronizálás:', err.response.status, err.response.data);
          } else {
            console.error('Szinkronizálási hiba:', err.message);
          }
        }
      }

      await refreshPendingCount();

      if (syncedCount > 0) {
        window.dispatchEvent(new Event('sync-completed'));
      }
    } finally {
      setSyncing(false);
    }
  }, [syncing, refreshPendingCount]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingEntries();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingEntries]);

  const savePendingEntry = useCallback(async (entryData) => {
    try {
      const db = await getDB();
      const { local_id, ...dataWithoutId } = entryData;
      const newId = await db.add('pending_entries', {
        ...dataWithoutId,
        created_offline_at: new Date().toISOString(),
        synced: false,
      });
      await refreshPendingCount();
      return newId;
    } catch (err) {
      console.error('savePendingEntry error:', err);
      throw err;
    }
  }, [refreshPendingCount]);

  const cacheLogbook = useCallback(async (logbook) => {
    const db = await getDB();
    await db.put('logbook_cache', logbook);
  }, []);

  const getCachedLogbook = useCallback(async (id) => {
    const db = await getDB();
    return db.get('logbook_cache', id);
  }, []);

  const getPendingEntries = useCallback(async (logbookId) => {
    const db = await getDB();
    const all = await db.getAll('pending_entries');
    return all.filter(e => e.logbook === logbookId && !e.synced);
  }, []);

  const updatePendingEntry = useCallback(async (local_id, updates) => {
    const db = await getDB();
    const entry = await db.get('pending_entries', local_id);
    if (entry) {
      await db.put('pending_entries', { ...entry, ...updates });
    }
  }, []);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      pendingCount,
      syncing,
      savePendingEntry,
      updatePendingEntry,
      cacheLogbook,
      getCachedLogbook,
      getPendingEntries,
      syncPendingEntries,
      refreshPendingCount,
    }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  return useContext(OfflineContext);
}